import { useEffect } from 'react';

// The User Tour SDK is loaded unversioned from the CDN, so a class rename there
// silently breaks the push-down. The guard test pins this; update both together.
export const USER_TOUR_BANNER_CLASS = 'usertour-widget-banner';
export const USER_TOUR_BANNER_VAR = '--usertour-banner-height';

const findBanner = (): HTMLElement | null =>
	(Array.from(document.body.children) as HTMLElement[]).find((el) =>
		Array.from(el.classList).some((c) => c.startsWith(USER_TOUR_BANNER_CLASS)),
	) ?? null;

// TopNav and the Design Studio editor header can each mount a BayUserTour. The root
// var is shared, so only the last mounted instance clears it on unmount.
let mountedCount = 0;

// The Act As bar owns the top edge (ActAsBanner, position: fixed, top: 0). Push the
// tour banner under it with margin-top, and take the same amount back with a negative
// margin-bottom so the page content below does not move (the hosts' body/wrap offsets
// already include the Act As height). `top` is the sticky offset once the page scrolls.
// Not a transform: the SDK's appear animation writes its own inline transform and wipes
// ours. !important beats the SDK's inline values.
const ACT_AS_OFFSET = 'var(--actas-banner-height, 0px)';
// Stacking: above the navs (<= 112), under the Act As bar (999), and under every
// modal, drawer, backdrop (Bootstrap 1035+) and full-screen loader (9999+). The SDK's
// default z-index would put the banner over all of those. The step lives in
// styles/theme_default/_zindex.scss ('usertour-banner'); hosts retune it by
// overriding the custom property, the literal is only the fallback.
export const USER_TOUR_BANNER_Z_INDEX = 'var(--bs-zindex-usertour-banner, 998)';
const BELOW_ACT_AS: Record<string, string> = {
	'margin-top': ACT_AS_OFFSET,
	'margin-bottom': `calc(-1 * ${ACT_AS_OFFSET})`,
	top: ACT_AS_OFFSET,
	'z-index': USER_TOUR_BANNER_Z_INDEX,
};
const placeBelowActAs = (banner: HTMLElement) => {
	for (const [prop, value] of Object.entries(BELOW_ACT_AS)) {
		if (banner.style.getPropertyValue(prop) !== value || banner.style.getPropertyPriority(prop) !== 'important') {
			banner.style.setProperty(prop, value, 'important');
		}
	}
};

/**
 * The "Top of page" tour banner is prepended to <body> in normal flow, so our
 * fixed top bars (Act As banner, TopNav, SideNav) would cover it. Order from the top
 * is Act As banner, tour banner, nav: the tour banner is pushed under the Act As bar,
 * and its live height is published on the ROOT (not body: TopNav watches body's style
 * attribute) so the navs can offset by it on top of --actas-banner-height. 0px when no
 * banner is showing.
 * Assumes the banner's "stick to top" setting is on, so it stays at the viewport top.
 */
const useUserTourBannerOffset = () => {
	useEffect(() => {
		const root = document.documentElement;
		mountedCount += 1;
		let banner: HTMLElement | null = null;
		let resizeObserver: ResizeObserver | undefined;
		// The SDK rewrites the banner's style attribute (appear animation, re-renders);
		// put the offset back whenever that happens.
		let styleObserver: MutationObserver | undefined;

		const publish = () => {
			const next = banner ? `${banner.getBoundingClientRect().height}px` : '0px';
			if (root.style.getPropertyValue(USER_TOUR_BANNER_VAR) !== next) {
				root.style.setProperty(USER_TOUR_BANNER_VAR, next);
			}
		};

		const sync = () => {
			const found = findBanner();
			if (found !== banner) {
				resizeObserver?.disconnect();
				styleObserver?.disconnect();
				banner = found;
				if (banner) {
					placeBelowActAs(banner);
					if (typeof MutationObserver !== 'undefined') {
						const target = banner;
						styleObserver = new MutationObserver(() => placeBelowActAs(target));
						styleObserver.observe(target, { attributes: true, attributeFilter: ['style'] });
					}
				}
				if (banner && typeof ResizeObserver !== 'undefined') {
					resizeObserver ??= new ResizeObserver(publish);
					resizeObserver.observe(banner);
				}
			}
			publish();
		};

		sync();
		const mutationObserver = typeof MutationObserver !== 'undefined' ? new MutationObserver(sync) : undefined;
		mutationObserver?.observe(document.body, { childList: true });

		return () => {
			mutationObserver?.disconnect();
			resizeObserver?.disconnect();
			styleObserver?.disconnect();
			mountedCount -= 1;
			if (mountedCount === 0) root.style.removeProperty(USER_TOUR_BANNER_VAR);
		};
	}, []);
};

export default useUserTourBannerOffset;
